import { useMemo } from "react";
import { formatMomentDate } from "../../utils/dateHelper";
import StatusChip from "../../components/statusChip/StatusChip";
import { BankIcons, getUserInitial } from "../../utils/otherHelopers";
import CustomTable from "../../components/table/CustomTable";

const NpaListSection = ({
  data,
  navigate,
  currentPage,
  getCustomerActions,
  isLoading,
  isInitialLoad,
  pagination,
}) => {

  const columns = useMemo(() => [
    {
      id: "property_id",
      header: "PROPERTY ID",
      cell: (_, row) => row?.property_number_id ? `# ${row?.property_number_id}` : "-"
    },
    {
      id: "borrower_name",
      header: "BORROWER NAME",
      cell: (_, row) => (
        <div className="d-flex align-items-center gap-2">
          <div className="profile-icon">
            {row?.borrower_names && getUserInitial(row?.borrower_names)}
          </div>
          <p className="ms-1 fw-medium text-capitalize mb-0">
            {row?.borrower_names ? row?.borrower_names : "-"}
          </p>
        </div>
      )
    },
    {
      id: "bank_name",
      header: "BANK NAME",
      className: "text-start",
      cell: (_, row) => row?.bank_user?.bank ? (
        <>
          {row?.bank_user?.bank?.logo?.url ? (
            <img
              src={row.bank_user.bank.logo.url}
              alt={row.bank_user.bank.name}
              style={{ width: "45px", height: "40px", marginRight: "6px" }}
            />
          ) : (
            BankIcons(row.bank_user.bank.name)
          )}
          {" "}
          {row.bank_user.bank.name}
        </>
      ) : "-"
    },
    {
      id: "reserve_price",
      header: "PRICE",
      className: "text-start",
      cell: (_, row) => row?.reserve_price ? `₹ ${row?.reserve_price}` : ""
    },
    {
      id: "bank_user",
      header: "BANK USER",
      className: "text-start",
      cell: (_, row) => row?.bank_user ? row?.bank_user?.name : "-"
    },
    {
      id: "auction_start_time",
      header: "AUCTION START",
      className: "text-start",
      cell: (_, row) => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <span>{row?.auction_start_time ? formatMomentDate(row.auction_start_time, "DD MMM YYYY") : ""}</span>
          <span>{row?.auction_start_time ? formatMomentDate(row.auction_start_time, "hh:mm A") : ""}</span>
        </div>
      )
    },
    {
      id: "auction_end_time",
      header: "AUCTION END",
      className: "text-start",
      cell: (_, row) => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <span>{row?.auction_end_time ? formatMomentDate(row.auction_end_time, "DD MMM YYYY") : ""}</span>
          <span>{row?.auction_end_time ? formatMomentDate(row.auction_end_time, "hh:mm A") : ""}</span>
        </div>
      )
    },
    {
      id: "status",
      header: "STATUS",
      cell: (_, row) => row?.status ? (
        <StatusChip
          bg={
            row?.status === "upcoming"
              ? "warning"
              : row?.status === "live"
                ? "success"
                : row?.status === "expired"
                  ? "danger"
                  : ""
          }
          pill
          title={row?.status}
          className="text-capitalize"
        />
      ) : "-"
    },
    {
      id: "asset_category",
      header: "ASSET CATEGORY",
      cell: (_, row) => row?.asset_category ? row?.asset_category?.name : "-"
    },
    {
      id: "possession_type",
      header: "POSSESSION TYPES",
      cell: (_, row) => row?.possession_type ? <StatusChip bg={"success"} pill title={row?.possession_type?.name} className="text-capitalize" /> : ""
    },
    {
      id: "property_type",
      header: "PROPERTY TYPE",
      cell: (_, row) => row?.property_type ? <StatusChip bg={"success"} pill title={row?.property_type?.name} className="text-capitalize" /> : ""
    },
    {
      id: "total_inquiries",
      header: "TOTAL INQUIRY",
      cell: (_, row) => row?.total_inquiries ? row?.total_inquiries : "-"
    }
  ], []);

  const actionList = useMemo(() => {
    // getCustomerActions returns actions array which can be directly mapped or formatted
    // Wait, getCustomerActions returns dynamic actions for specific items. But CustomTable requires actionList to be a static array
    // where each action object can have handler: (row) => ... and isVisible: (row) => ...
    // Let's adapt getCustomerActions!
    return [
      {
        title: "View",
        handler: (row) => navigate(`/npa-properties/property-details/${row.id}`)
      },
      {
        title: "Edit",
        isVisible: (row) => {
          const actions = getCustomerActions(row);
          return actions.some(a => a.title === "Edit");
        },
        handler: (row) => {
          const actions = getCustomerActions(row);
          const editAction = actions.find(a => a.title === "Edit");
          if (editAction) editAction.handler();
        }
      },
      {
        title: "Delete",
        isVisible: (row) => {
          const actions = getCustomerActions(row);
          return actions.some(a => a.title === "Delete");
        },
        handler: (row) => {
          const actions = getCustomerActions(row);
          const deleteAction = actions.find(a => a.title === "Delete");
          if (deleteAction) deleteAction.handler();
        }
      }
    ];
  }, [getCustomerActions, navigate]);

  return (
    <CustomTable
      data={data || []}
      columns={columns}
      loading={isLoading && !isInitialLoad}
      emptyMessage="No properties found"
      keyField="id"
      actionList={actionList}
      rowClick={(row) => navigate(`/npa-properties/property-details/${row?.id}`, { state: { currentPage } })}
      pagination={pagination}
    />
  );
};

export default NpaListSection;
